// src/follower/components/TradeHistoryPanel.jsx
// Phase 1 — reads from mockStatus.closed + mockExplanations. No backend dependency.
// Section D of myTrades page.

import React, { useState } from "react";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    bg2:    "#101b2e",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso) {
    if (!iso) return "--";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", {
            hour: "2-digit", minute: "2-digit", second: "2-digit",
            hour12: false, timeZone: "America/New_York",
        }) + " ET";
}

function formatPnL(val) {
    if (val === null || val === undefined) return "--";
    const abs = Math.abs(val).toFixed(2);
    return val >= 0 ? `+$${abs}` : `-$${abs}`;
}

function closeReasonLabel(reason) {
    switch (reason) {
        case "target_hit":           return "Target Hit";
        case "stop_loss":            return "Stop Loss";
        case "no_expansion_timeout": return "Early Exit";
        case "stock_closed":         return "Market Close";
        default:                     return reason || "--";
    }
}

function closeReasonColor(reason) {
    switch (reason) {
        case "target_hit":           return C.green;
        case "stop_loss":            return C.red;
        case "no_expansion_timeout": return C.amber;
        case "stock_closed":         return C.meta;
        default:                     return C.meta;
    }
}

function ExplanationText({ text }) {
    if (!text) return null;
    const lines    = text.split("\n");
    const headline = lines[0];
    const bullets  = lines.slice(1).filter(l => l.trim());
    return (
        <div>
            <div style={{ fontFamily: FONT, fontSize: 10, color: C.body, marginBottom: bullets.length ? 6 : 0 }}>
                {headline}
            </div>
            {bullets.map((line, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 3 }}>
                <div style={{ width: 3, height: 3, borderRadius: "50%", background: C.cyan, marginTop: 5, flexShrink: 0 }} />
          <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta, lineHeight: 1.6 }}>
            {line.replace(/^\s*-\s*/, "")}
          </span>
            </div>
                ))}
        </div>
    );
}

// ─── Trade row ────────────────────────────────────────────────────────────────

function TradeRow({ position, explanation }) {
    const [expanded, setExpanded] = useState(false);

    const isCall    = position.option_type === "call";
    const typeColor = isCall ? C.green : C.red;
    const typeLabel = isCall ? "CALL" : "PUT";
    const pnlVal    = position.realized_pnl;
    const pnlColor  = pnlVal > 0 ? C.green : pnlVal < 0 ? C.red : C.meta;
    const reason    = explanation?.close_reason || "";
    const hasExplanation = explanation?.entry_explanation || explanation?.exit_explanation;

    return (
        <div style={{
      background:   C.bg2,
      border:       `1px solid ${C.border}`,
      borderRadius: 8,
      overflow:     "hidden",
    }}>

            {/* ── Main row ── */}
            <div style={{
        padding:    "12px 18px",
        display:    "flex",
        alignItems: "center",
        gap:        14,
        flexWrap:   "wrap",
      }}>

                {/* Type badge */}
                <div style={{
          padding:       "3px 10px",
          borderRadius:  3,
          border:        `1px solid ${typeColor}30`,
          background:    typeColor + "12",
          fontFamily:    FONT,
          fontSize:      9,
          fontWeight:    500,
          letterSpacing: "1px",
          color:         typeColor,
          flexShrink:    0,
        }}>
                    {typeLabel}
                </div>

                {/* Contract + time */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 160 }}>
          <span style={{ fontFamily: FONT, fontSize: 11, color: C.text, letterSpacing: ".3px" }}>
            {position.contract_symbol}
          </span>
          <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: ".5px" }}>
            {formatTime(position.closed_at)}
          </span>
                </div>

                {/* Qty */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Qty</span>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.text }}>{position.qty}</span>
                </div>

                {/* Entry fill */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Entry</span>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.text }}>${position.entry_fill_price.toFixed(2)}</span>
                </div>

                {/* Arrow */}
                <span style={{ fontFamily: FONT, fontSize: 10, color: C.meta }}>→</span>

                {/* Exit fill */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Exit</span>
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.text }}>${position.exit_fill_price.toFixed(2)}</span>
                </div>

                {/* Close reason */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Reason</span>
          <span style={{ fontFamily: FONT, fontSize: 10, color: closeReasonColor(reason), letterSpacing: ".3px" }}>
            {closeReasonLabel(reason)}
          </span>
                </div>

                {/* Realized P&L */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>P&L</span>
          <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: pnlColor, letterSpacing: ".3px" }}>
            {formatPnL(pnlVal)}
          </span>
                </div>

                {/* Expand toggle */}
                {hasExplanation && (
                <button
                    onClick={() => setExpanded(v => !v)}
                    style={{
              marginLeft:    "auto",
              fontFamily:    FONT,
              fontSize:      8,
              letterSpacing: "1px",
              color:         expanded ? C.cyan : C.meta,
              background:    "transparent",
              border:        `1px solid ${expanded ? C.cyan + "40" : C.border}`,
              borderRadius:  3,
              padding:       "3px 9px",
              cursor:        "pointer",
              transition:    "color .15s, border-color .15s",
              flexShrink:    0,
            }}
                >
                    {expanded ? "HIDE ▲" : "DETAIL ▼"}
                </button>
                    )}

            </div>

            {/* ── Explanation drawer ── */}
            {expanded && (
            <div style={{ borderTop: `1px solid ${C.border}`, background: C.bg1 }}>

                {/* Entry explanation */}
                {explanation?.entry_explanation && (
                    <div style={{ padding: "12px 18px", borderBottom: explanation?.exit_explanation ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ fontFamily: FONT, fontSize: 8, color: C.cyan, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>
                    Entry Reasoning
                    </div>
                    <ExplanationText text={explanation.entry_explanation} />
                    </div>
                    )}

                {/* Exit explanation */}
                {explanation?.exit_explanation && (
                    <div style={{ padding: "12px 18px" }}>
                    <div style={{ fontFamily: FONT, fontSize: 8, color: closeReasonColor(reason), letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8 }}>
                    Exit — {closeReasonLabel(reason)}
                    </div>
                    <ExplanationText text={explanation.exit_explanation} />
                    {explanation.reactive_signal && (
                    <div style={{ marginTop: 8, fontFamily: FONT, fontSize: 9, color: C.amber, letterSpacing: ".3px" }}>
                        Reactive signal: {explanation.reactive_signal}
                    </div>
                        )}
                    </div>
                    )}

            </div>
                )}

        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
//
// Props:
//   positions    — array from mockStatus.closed / GET /follower/status
//   explanations — dict keyed by master_position_id from mockExplanations
//   dailyPnL     — number, today's total realized P&L

export default function TradeHistoryPanel({ positions, explanations, dailyPnL }) {
    // Newest first
    const seen   = new Set();
    const unique = positions.filter(p => {
        if (seen.has(p.master_position_id)) return false;
        seen.add(p.master_position_id);
        return true;
    });
    const sorted = unique.sort((a, b) => new Date(b.closed_at) - new Date(a.closed_at));

    const totalPnL     = dailyPnL ?? 0;
    const pnlColor     = totalPnL > 0 ? C.green : totalPnL < 0 ? C.red : C.meta;
    const winCount     = unique.filter(p => (p.realized_pnl ?? 0) > 0).length;
    const lossCount    = unique.filter(p => (p.realized_pnl ?? 0) < 0).length;

    return (
        <>
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
      `}</style>

        <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>

            {/* ── Panel header ── */}
            <div style={{
          padding:        "14px 24px",
          borderBottom:   `1px solid ${C.border}`,
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          flexWrap:       "wrap",
          gap:            12,
        }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 3, height: 16, borderRadius: 2, background: `linear-gradient(180deg, ${C.cyan}, ${C.cyan}40)` }} />
            <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, color: C.text, letterSpacing: ".5px" }}>
              Trade History
            </span>
            <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: ".3px" }}>
              — Today
            </span>
                </div>

                {/* Summary stats */}
                {positions.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ display: "flex", gap: 10 }}>
                        <span style={{ fontFamily: FONT, fontSize: 9, color: C.green }}>{winCount}W</span>
                        <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta }}>/</span>
                        <span style={{ fontFamily: FONT, fontSize: 9, color: C.red }}>{lossCount}L</span>
                    </div>
                    <div style={{ width: 1, height: 16, background: C.border }} />
              <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 500, color: pnlColor, letterSpacing: ".3px" }}>
                {formatPnL(totalPnL)}
              </span>
                </div>
                    )}
            </div>

            {/* ── Body ── */}
            <div style={{ padding: "16px" }}>
                {sorted.length === 0 ? (
                <div style={{
              padding: "40px 24px", textAlign: "center",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
            }}>
                    <div style={{
                width: 40, height: 40, borderRadius: "50%",
                border: `1px solid ${C.border}`, display: "grid", placeItems: "center", marginBottom: 4,
              }}>
                        <div style={{ width: 12, height: 12, borderRadius: "50%", border: `1px solid ${C.meta}`, opacity: .4 }} />
                    </div>
              <span style={{ fontFamily: FONT, fontSize: 10, color: C.meta, letterSpacing: ".3px" }}>
                No closed trades today
              </span>
                </div>
                    ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {sorted.map(pos => (
                    <TradeRow
                        key={pos.master_position_id}
                        position={pos}
                        explanation={explanations?.[pos.master_position_id]}
                    />
                        ))}
                </div>
                    )}
            </div>

        </div>
        </>
    );
}